import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { ArrowLeft, Bell, BellOff, Mail } from "lucide-react";
import PathBackdrop from "@/components/career/PathBackdrop";

export default function GentleReminders() {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/reminders/settings");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const next = !data.enabled;
      await api.patch("/reminders/settings", { enabled: next });
      setData({ ...data, enabled: next });
      toast.success(next ? t("gr.toast_on") : t("gr.toast_off"));
    } catch {} finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>;
  }

  const p = data.preview;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="reminders-page">
      <PathBackdrop />

      <Link to="/profile" className="ghost-btn text-xs" data-testid="reminders-back">
        <ArrowLeft size={12} /> {t("nav.profile") || "Profile"}
      </Link>

      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Bell size={12} /> {t("gr.title")}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{t("gr.title")}</h1>
        <p className="text-stone-400 mt-2 italic">{t("gr.tagline")}</p>
      </div>

      {/* Toggle */}
      <div className="paper p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${
              data.enabled ? "bg-emerald-500" : "bg-stone-600"
            }`} />
            <span className="serif text-xl text-stone-100">
              {data.enabled ? t("gr.status_on") : t("gr.status_off")}
            </span>
          </div>
          <button
            onClick={toggle}
            disabled={saving}
            data-testid="reminders-toggle"
            className={data.enabled ? "ghost-btn" : "pill-btn"}
          >
            {data.enabled ? <BellOff size={14} /> : <Bell size={14} />}
            {data.enabled ? t("gr.disable") : t("gr.enable")}
          </button>
        </div>
        <p className="text-stone-400 text-sm mt-3">{t("gr.explainer")}</p>
      </div>

      {/* Preview */}
      <div className="paper p-5" data-testid="reminders-preview">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={12} className="text-amber-500" />
          <div className="label-cap">{t("gr.preview_title")}</div>
        </div>

        {!p ? (
          <div className="text-stone-500 text-sm italic">{t("gr.no_active_prev")}</div>
        ) : (
          <div className="space-y-3">
            <div className="serif text-lg text-stone-100">{t("gr.preview_next_step")}</div>
            <div className="serif text-2xl text-stone-100">{p.step_name}</div>
            <div className="pt-3 border-t border-stone-800">
              <div className="mono text-[10px] tracking-widest text-amber-500 uppercase">
                {t("gr.preview_why")}
              </div>
              <div className="text-stone-300 text-sm mt-1">
                {t(p.reason_key).replace("{goal}", p.goal_title || "—")}
              </div>
            </div>
            <div className="pt-3 border-t border-stone-800 text-sm">
              <div className="mono text-[10px] tracking-widest text-stone-500 uppercase">
                {t("gr.preview_your_goal")}
              </div>
              <div className="text-stone-100 mt-1">{p.goal_title}</div>
              {p.goal_why && (
                <>
                  <div className="mono text-[10px] tracking-widest text-stone-500 uppercase mt-2">
                    {t("gr.preview_your_why")}
                  </div>
                  <div className="text-stone-300 italic mt-1">„{p.goal_why}"</div>
                </>
              )}
            </div>
            <div className="pt-3 text-xs italic text-stone-500">{t("gr.gentle_note")}</div>
          </div>
        )}
      </div>
    </div>
  );
}
